"use client";

import React, { ReactNode } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";

export interface SortableListItem {
  id: string;
}

interface SortableListRenderProps<T extends SortableListItem> {
  item: T;
  index: number;
  isDragging: boolean;
  dragHandle: ReactNode;
}

interface SortableListProps<T extends SortableListItem> {
  items: T[];
  onReorder: (items: T[]) => void;
  renderItem: (props: SortableListRenderProps<T>) => ReactNode;
  emptyMessage?: string;
  listClassName?: string;
  itemClassName?: string | ((props: { item: T; index: number; isDragging: boolean }) => string);
  dragHandleLabel?: string;
}

interface SortableListRowProps<T extends SortableListItem> {
  item: T;
  index: number;
  renderItem: (props: SortableListRenderProps<T>) => ReactNode;
  itemClassName?: string | ((props: { item: T; index: number; isDragging: boolean }) => string);
  dragHandleLabel: string;
}

function SortableListRow<T extends SortableListItem>({
  item,
  index,
  renderItem,
  itemClassName,
  dragHandleLabel,
}: SortableListRowProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  const dragHandle = (
    <button
      type="button"
      className="inline-flex h-9 w-9 flex-shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-current opacity-70 transition hover:opacity-100 active:cursor-grabbing"
      title={dragHandleLabel}
      aria-label={dragHandleLabel}
      {...attributes}
      {...listeners}
    >
      <svg
        className="h-5 w-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
      </svg>
    </button>
  );

  const resolvedItemClassName =
    typeof itemClassName === "function"
      ? itemClassName({ item, index, isDragging })
      : itemClassName;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "transition-[box-shadow,opacity,transform] duration-200",
        isDragging && "opacity-90 shadow-xl",
        resolvedItemClassName,
      )}
    >
      {renderItem({ item, index, isDragging, dragHandle })}
    </div>
  );
}

export default function SortableList<T extends SortableListItem>({
  items,
  onReorder,
  renderItem,
  emptyMessage = "No items added yet.",
  listClassName = "space-y-3",
  itemClassName,
  dragHandleLabel = "Drag to reorder",
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  if (!items.length) {
    return (
      <div className="rounded-md border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className={listClassName}>
          {items.map((item, index) => (
            <SortableListRow
              key={item.id}
              item={item}
              index={index}
              renderItem={renderItem}
              itemClassName={itemClassName}
              dragHandleLabel={dragHandleLabel}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
